import { FormikDebounce } from '../common/form/FormikDebounce';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  getQuestionnaireQuestions,
  selectQuestionnaireQuestions,
  selectQuestionnaireQuestionsState
} from '../../store/entities/questionnaireQuestions';
import { mediaApi } from '../../utils/api';
import { DetailHeader } from '../common/DetailHeader';
import * as Yup from 'yup';
import { validations } from '../../utils/validations';
import { FormContent } from './QuestionnaireQuestionsFormContent';
import { selectQuestionnaire } from '../../store/entities/questionnaire';
import { QcsButton } from '../common/basic/QcsButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getLangNameObj } from '../../utils/format';
import { useNavigation } from '../../utils/urlPaths';
import { QuestionDto } from '@api/models/questionnaire/QuestionDto';
import { questionnaireVersionService } from '@api/services/services';
import { ShowResourceContent } from '../ShowResourceContent';
import { resources } from '@api/resources';

export interface QuestionsForm {
  questions: QuestionDto[];
}

export const QuestionnaireQuestions: FC = () => {
  const { t, i18n } = useTranslation();
  const { questionnaireId, questionnaireVersionId, languageCode } = useParams();
  const { goBack, getBackPath } = useNavigation(`/questionnaire/${questionnaireId}/${questionnaireVersionId}`);
  const dispatch = useAppDispatch();
  const questions = useAppSelector(selectQuestionnaireQuestions);
  const questionnaire = useAppSelector(selectQuestionnaire);
  const [oldFilesToRemove, setOldFilesToRemove] = useState<string[]>([]);

  useEffect(() => {
    dispatch(getQuestionnaireQuestions(questionnaireVersionId!, languageCode!));
  }, [dispatch, languageCode, questionnaireVersionId]);

  const handleSubmit = async (formData: QuestionsForm) => {
    await questionnaireVersionService.updateAllQuestions(questionnaireVersionId!, languageCode!, formData.questions);

    for (const oldFile of oldFilesToRemove) {
      try {
        await mediaApi.deleteFile(oldFile);
      } catch {
        //Ignore error.
      }
    }
  };

  const handleRemoveOldFile = (document: string) => {
    setOldFilesToRemove([...oldFilesToRemove, document]);
  };

  return (
    <>
      <DetailHeader title={`${t('questionnaire.questions.title')}: ${getLangNameObj(i18n, questionnaire)}`} backTo={getBackPath} />
      <ShowResourceContent resource={resources.getQuestions} fetchStateSelector={selectQuestionnaireQuestionsState}>
        <FormikDebounce<QuestionsForm>
          initialValues={{ questions }}
          onSubmit={handleSubmit}
          validateOnChange={false}
          validationSchema={Yup.object().shape({
            questions: Yup.array(
              Yup.object().shape({
                content: validations.stringRequired(t),
                answers: Yup.array(
                  Yup.object().shape({
                    content: validations.stringRequired(t),
                  }),
                ).test({
                  message: t('questionnaire.questions.errorAnswerRequired')!,
                  test: (val: any) => {
                    return val.some((x: any) => x.correct);
                  },
                }),
              }),
            ),
          })}
        >
          <FormContent onRemoveOldFile={handleRemoveOldFile} />
        </FormikDebounce>
      </ShowResourceContent>
      <QcsButton sx={{ width: 'fit-content' }} variant="outlined" onClick={() => goBack()}>
        <ArrowBackIcon />
        {t('common.back')}
      </QcsButton>
    </>
  );
};
